import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { dateValidatorNullable, nonNegativeIntValidator } from '@/schemas/common/validators';

/**
 * GET /api/wallet/earnings/summary
 * Returns total net earnings and statistics for completed transactions
 */

/**
 * Earnings summary data schema
 */
export const EarningsSummarySchema = z.object({
  /** Total net earnings after commission in decimal format (e.g., "1234.56") */
  totalNet: z.string().regex(/^\d+\.\d{2}$/, 'Total net must be a decimal with 2 decimal places'),
  /** Number of completed transactions */
  transactionCount: nonNegativeIntValidator,
  /** Date of first completed sale */
  firstSaleDate: dateValidatorNullable,
  /** Date of most recent completed sale */
  lastSaleDate: dateValidatorNullable,
  /** Yearly average earnings in decimal format */
  yearlyAverage: z.string().regex(/^\d+\.\d{2}$/, 'Yearly average must be a decimal with 2 decimal places'),
});

export type EarningsSummary = z.infer<typeof EarningsSummarySchema>;

/**
 * Success response schema for earnings summary
 */
export const GetEarningsSummarySuccessSchema = z.object({
  success: z.literal(true),
}).merge(EarningsSummarySchema);

export type GetEarningsSummarySuccess = z.infer<typeof GetEarningsSummarySuccessSchema>;

/**
 * Complete response schema for earnings summary
 * Discriminated union of success and error responses
 */
export const GetEarningsSummaryResponseSchema = z.discriminatedUnion('success', [
  GetEarningsSummarySuccessSchema,
  ErrorResponseSchema,
]);

export type GetEarningsSummaryResponse = z.infer<typeof GetEarningsSummaryResponseSchema>;

/**
 * GET /api/wallet/earnings/chart-data?year=2024
 * Returns monthly earnings breakdown for a specified year
 */

/**
 * Monthly earnings data schema
 */
export const MonthlyEarningsSchema = z.object({
  /** Short month name (e.g., "Jan", "Feb") */
  month: z.string().length(3, 'Month must be a 3-letter abbreviation'),
  /** Month number 1-12 */
  monthNumber: z.number().int().min(1).max(12),
  /** Net earnings for this month in decimal format */
  earnings: z.string().regex(/^\d+\.\d{2}$/, 'Earnings must be a decimal with 2 decimal places'),
  /** Number of completed transactions in this month */
  count: nonNegativeIntValidator,
});

export type MonthlyEarnings = z.infer<typeof MonthlyEarningsSchema>;

/**
 * Success response schema for earnings chart data
 */
export const GetEarningsChartDataSuccessSchema = z.object({
  success: z.literal(true),
  /** Year for which data is returned */
  year: z.number().int().min(2000).max(2100),
  /** Array of 12 months with earnings data */
  monthlyData: z.array(MonthlyEarningsSchema).length(12, 'Monthly data must contain exactly 12 months'),
  /** Array of years that have transaction data */
  availableYears: z.array(z.number().int().min(2000).max(2100)),
});

export type GetEarningsChartDataSuccess = z.infer<typeof GetEarningsChartDataSuccessSchema>;

/**
 * Complete response schema for earnings chart data
 * Discriminated union of success and error responses
 */
export const GetEarningsChartDataResponseSchema = z.discriminatedUnion('success', [
  GetEarningsChartDataSuccessSchema,
  ErrorResponseSchema,
]);

export type GetEarningsChartDataResponse = z.infer<typeof GetEarningsChartDataResponseSchema>;
